import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, TrendingUp, TrendingDown, Minus, Search, CheckCircle2, XCircle, Target, Shield, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Markets available to choose
const MARKETS = [
  { value: "XAU/USD", label: "Gold (XAU/USD)" },
  { value: "EUR/USD", label: "EUR/USD" },
  { value: "GBP/USD", label: "GBP/USD" },
  { value: "USD/JPY", label: "USD/JPY" },
  { value: "AUD/USD", label: "AUD/USD" },
  { value: "USD/CAD", label: "USD/CAD" },
  { value: "USD/CHF", label: "USD/CHF" },
  { value: "NZD/USD", label: "NZD/USD" },
  { value: "EUR/GBP", label: "EUR/GBP" },
  { value: "EUR/JPY", label: "EUR/JPY" },
  { value: "GBP/JPY", label: "GBP/JPY" },
];

export function TiaScalpingPage({ lang = "en", user = {}, simpleOnly = false }) {
  const isAr = lang === "ar";
  const [symbol, setSymbol] = useState("XAU/USD");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Advanced view if user is admin or has tier_advanced (unless caller forces simpleOnly)
  const isAdvanced = !simpleOnly && (user?.role === "admin" || user?.tier_advanced || user?.signal_tier === "advanced");

  const analyze = async () => {
    setLoading(true);
    setError("");
    try {
      const urlSymbol = symbol.replace("/", "-");
      const res = await axios.get(`${API}/tia-scalping/${urlSymbol}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || (isAr ? "تعذّر جلب الإشارة" : "Failed to load signal"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setData(null); }, [symbol]);

  const sig = data?.signal || "NEUTRAL";
  const sigColor = sig === "BUY" ? "bg-emerald-500" : sig === "SELL" ? "bg-red-500" : "bg-zinc-400";
  const SigIcon = sig === "BUY" ? TrendingUp : sig === "SELL" ? TrendingDown : Minus;

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="space-y-4">
      {/* Header */}
      <Card className="border-4 border-pink-300 bg-gradient-to-br from-amber-50 via-pink-50 to-purple-50 rounded-3xl shadow-lg">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-3xl">🎯</div>
            <div>
              <h2 className="text-lg sm:text-xl font-extrabold text-pink-700">
                {isAr ? "إستراتيجية تيا للسكالبينج" : "Tia Scalping Strategy"}
              </h2>
              <p className="text-[11px] sm:text-xs text-zinc-600">
                {isAr
                  ? "SMC + EMA200 على M15 — تنفيذ على M1/M5"
                  : "SMC + EMA200 on M15 — execution on M1/M5"}
              </p>
            </div>
          </div>

          {/* Market selector + Analyze */}
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <select
              value={symbol}
              onChange={(e) => setSymbol(e.target.value)}
              data-testid="tia-symbol-select"
              className="flex-1 px-3 py-2.5 rounded-2xl border-2 border-pink-300 bg-white text-sm font-semibold text-zinc-800 focus:outline-none focus:border-pink-500"
            >
              {MARKETS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <Button
              onClick={analyze}
              disabled={loading}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-6 py-2.5 rounded-2xl shadow-md"
              data-testid="tia-analyze-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              {isAr ? "حلّل الآن" : "Analyze Now"}
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-2">
            {isAr
              ? "السكالبر يقرأ M15 (الاتجاه) + M5 (هيكل السوق) + M1 (التنفيذ) — اختر السوق فقط."
              : "Engine reads M15 (trend) + M5 (structure) + M1 (execution). Pick the market only."}
          </p>

          {error && (
            <div className="mt-3 bg-red-50 border-2 border-red-200 text-red-700 p-3 rounded-2xl text-sm" data-testid="tia-error">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Signal result */}
      {data && (
        <Card className="border-4 border-pink-300 bg-white rounded-3xl shadow-lg overflow-hidden" data-testid="tia-signal-card">
          <div className={`h-2 ${sigColor}`} />
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3">
              <Badge className={`${sigColor} text-white text-base px-4 py-1.5 rounded-full font-bold`}>
                <SigIcon className="w-4 h-4 mr-1.5" />
                {sig}
              </Badge>
              <div className="text-right">
                <p className="text-[10px] uppercase text-zinc-500">{isAr ? "الثقة" : "Confidence"}</p>
                <p className="text-2xl font-bold text-pink-600" data-testid="tia-confidence">{data.confidence}%</p>
              </div>
            </div>

            {/* Low confidence risk warning */}
            {typeof data.confidence === "number" && data.confidence < 70 && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 text-xs text-amber-800 mb-3" data-testid="tia-risk-notice">
                <p className="font-bold mb-1">⚠ {isAr ? "تحذير مخاطرة" : "Risk Warning"}</p>
                <p>
                  {isAr
                    ? `الثقة منخفضة (${data.confidence}%). يُفضّل الانتظار لإشارة أقوى أو تقليل حجم الصفقة.`
                    : `Confidence is low (${data.confidence}%). Consider waiting for a stronger setup or reducing position size.`}
                </p>
              </div>
            )}

            {/* Core SL / TP / Entry */}
            {sig !== "NEUTRAL" && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
                <CellBox label={isAr ? "السعر الحالي" : "Current"} value={data.current_price} color="zinc" />
                <CellBox label={isAr ? "الدخول" : "Entry"} value={data.entry} color="blue" bold />
                <CellBox label="SL" value={data.stop_loss} color="red" bold />
                <CellBox label="TP1 (1:1.5)" value={data.take_profit_1} color="emerald" />
                <CellBox label="TP2 (1:2)" value={data.take_profit_2} color="emerald" />
              </div>
            )}
            {sig === "NEUTRAL" && (
              <div className="bg-zinc-50 border-2 border-zinc-200 rounded-xl p-3 text-xs text-zinc-700 mb-3">
                {isAr
                  ? "لا توجد إشارة سكالب الآن — انتظر تأكيد الاتجاه والهيكل وشمعة الدخول."
                  : "No scalp setup right now — wait for trend, structure, and entry-candle confirmation."}
              </div>
            )}

            {/* Advanced details */}
            {isAdvanced && (
              <AdvancedDetails data={data} isAr={isAr} />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AdvancedDetails({ data, isAr }) {
  return (
    <div className="space-y-3 mt-2 border-t border-zinc-200 pt-3">
      {/* Steps grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <StepBox
          icon={<TrendingUp className="w-4 h-4" />}
          title={isAr ? "1. الاتجاه (M15 + EMA200)" : "1. Trend (M15 EMA200)"}
          ok={data.allowed_direction !== null}
          value={`${data.trend} · EMA200=${data.ema_200_m15}`}
        />
        <StepBox
          icon={<Target className="w-4 h-4" />}
          title={isAr ? "2. هيكل M5 (BOS / CHoCH)" : "2. M5 Structure (BOS/CHoCH)"}
          ok={data.structure_direction === data.allowed_direction && data.allowed_direction !== null}
          value={data.structure}
        />
        <StepBox
          icon={<Shield className="w-4 h-4" />}
          title={isAr ? "3. Pullback (OB / FVG)" : "3. Pullback (OB / FVG)"}
          ok={data.in_order_block || data.in_fvg}
          value={
            data.in_order_block
              ? `OB ${data.order_block?.type} (${data.order_block?.low} – ${data.order_block?.high})`
              : data.in_fvg
              ? `FVG ${data.fvg?.type} (${data.fvg?.low} – ${data.fvg?.high})`
              : isAr ? "لا يوجد سحب" : "No active pullback"
          }
        />
        <StepBox
          icon={<CheckCircle2 className="w-4 h-4" />}
          title={isAr ? "4. شمعة التأكيد" : "4. Entry Candle"}
          ok={Boolean(data.engulfing) || Boolean(data.pin_bar)}
          value={
            data.engulfing
              ? `${data.engulfing} engulfing`
              : data.pin_bar
              ? `${data.pin_bar} pin bar`
              : isAr ? "لا يوجد تأكيد" : "No confirmation candle yet"
          }
        />
        <StepBox
          icon={<Clock className="w-4 h-4" />}
          title={isAr ? "5. الجلسة" : "5. Trading Session"}
          ok={data.session_ok}
          value={data.session}
        />
      </div>

      {/* Reasons pass / fail */}
      {(data.reasons_pass?.length > 0 || data.reasons_fail?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {data.reasons_pass?.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 text-xs">
              <p className="font-bold text-emerald-700 mb-1">{isAr ? "✓ النقاط المحققة" : "✓ Conditions Met"}</p>
              <ul className="space-y-0.5 text-emerald-800">
                {data.reasons_pass.map((r, i) => <li key={i}>• {r}</li>)}
              </ul>
            </div>
          )}
          {data.reasons_fail?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 text-xs">
              <p className="font-bold text-red-700 mb-1">{isAr ? "✗ النقاط المفقودة" : "✗ Conditions Missing"}</p>
              <ul className="space-y-0.5 text-red-800">
                {data.reasons_fail.map((r, i) => <li key={i}>• {r}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Risk management & checklist */}
      <div className="bg-pink-50 border border-pink-200 rounded-xl p-3 text-xs text-pink-900">
        <p className="font-bold mb-1.5">{isAr ? "إدارة المخاطر" : "Risk Management"}</p>
        <ul className="space-y-0.5 ml-3 list-disc">
          <li>{isAr ? "1% كحد أقصى لكل صفقة" : "Risk max 1% per trade"}</li>
          <li>{isAr ? "2-3 صفقات كحد أقصى لكل جلسة" : "Max 2–3 trades per session"}</li>
          <li>{isAr ? "توقف بعد خسارتين متتاليتين" : "Stop after 2 consecutive losses"}</li>
          <li>{isAr ? "تجنب الأخبار عالية التأثير (CPI, NFP, FOMC)" : "Avoid high-impact news (CPI, NFP, FOMC)"}</li>
        </ul>
      </div>

      {/* Golden rule footer */}
      <div className="text-[11px] text-center text-zinc-500 italic">
        {isAr
          ? "القاعدة الذهبية: الاتجاه ← الهيكل ← السحب ← التأكيد ← الدخول ← إدارة المخاطر"
          : "Golden Rule: Trend → Structure → Pullback → Confirmation → Entry → Manage Risk"}
      </div>
    </div>
  );
}

function CellBox({ label, value, color = "zinc", bold = false }) {
  const colorMap = {
    zinc: "bg-zinc-50 border-zinc-200 text-zinc-800",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red: "bg-red-50 border-red-200 text-red-700",
  };
  return (
    <div className={`rounded-xl border-2 p-2 text-center ${colorMap[color]}`}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className={`text-xs sm:text-sm font-mono ${bold ? "font-bold" : ""}`}>{value ?? "-"}</p>
    </div>
  );
}

function StepBox({ icon, title, ok, value }) {
  return (
    <div className={`flex items-start gap-2 p-2.5 rounded-xl border-2 ${ok ? "bg-emerald-50 border-emerald-200" : "bg-zinc-50 border-zinc-200"}`}>
      <div className={`shrink-0 mt-0.5 ${ok ? "text-emerald-600" : "text-zinc-400"}`}>
        {ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-bold text-zinc-800">{title}</p>
        <p className="text-[11px] text-zinc-600 break-words">{value}</p>
      </div>
    </div>
  );
}
